import classNames from 'classnames';

import type { HitType, SearchHit as SearchHitType } from '~/types/search';

import Highlighter from '@helsenorge/designsystem-react/components/Highlighter';
import Icon, { IconSize } from '@helsenorge/designsystem-react/components/Icon';
import ArrowRight from '@helsenorge/designsystem-react/components/Icons/ArrowRight';
import Tag, { type TagVariant } from '@helsenorge/designsystem-react/components/Tag';
import Title from '@helsenorge/designsystem-react/components/Title';
import { usePseudoClasses } from '@helsenorge/designsystem-react/hooks/usePseudoClasses';
import { palette } from '@helsenorge/designsystem-react/theme/palette';

import { getSearchHitUrl } from './utils';

import styles from './styles.module.scss';

interface SearchHitProps {
  hit: SearchHitType;
  last?: boolean;
  returnUrl: string;
  returnUrlText: string;
  onClick?: () => void;
  tjenesteHitTypeText: string;
  verktoyHitTypeText: string;
}

type TagType = Extract<HitType, 'verktoy' | 'tjeneste'>;

const validTags: Record<TagType, keyof typeof TagVariant> = {
  verktoy: 'accent1',
  tjeneste: 'accent2',
};

const isValidTagType = (tag: string): tag is TagType => tag in validTags;

const SearchHit: React.FC<SearchHitProps> = props => {
  const { refObject: hoverRef, isHovered, isFocused } = usePseudoClasses<HTMLDivElement>();

  const url = getSearchHitUrl(props.hit.url, props.returnUrlText, props.returnUrl);
  const highlightedWords = props.hit.introHighlights?.join('|') ?? '';

  const hitType = props.hit.hitType;

  const hitTypeText: Record<TagType, string> = {
    verktoy: props.verktoyHitTypeText,
    tjeneste: props.tjenesteHitTypeText,
  };

  return (
    <div ref={hoverRef} className={classNames(styles['search-hit'], props.last ? styles['search-hit--last'] : '')}>
      {isValidTagType(hitType) && (
        <div className={styles['search-hit__tag']}>
          <Tag variant={validTags[hitType]}>{hitTypeText[hitType]}</Tag>
          <Icon size={IconSize.XSmall} color={palette.blueberry600} svgIcon={ArrowRight} isHovered={isHovered || isFocused} />
        </div>
      )}
      <div className={styles['search-hit__header']}>
        <Title htmlMarkup="h2" appearance="title3">
          <a href={url} className={styles['search-hit__link']} onClick={props.onClick}>
            {props.hit.title}
          </a>
        </Title>
        {!isValidTagType(hitType) && (
          <Icon size={IconSize.XSmall} color={palette.blueberry600} svgIcon={ArrowRight} isHovered={isHovered || isFocused} />
        )}
      </div>
      <Highlighter searchText={highlightedWords}>
        <p className={styles['search-hit__intro']}>{props.hit.intro}</p>
      </Highlighter>
    </div>
  );
};

export default SearchHit;
